import { toastServerError } from "helpers";
import { useState } from "react";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Modal, Select } from "ui";

import { useEnrollMarketingFlowContact, useMarketingContacts } from "../../apis";

import styles from "./EnrollContactModal.module.scss";

interface EnrollContactModalProps {
  isOpen: boolean;
  flowId: string;
  onClose: () => void;
}

const EnrollContactModal = ({
  isOpen,
  flowId,
  onClose
}: EnrollContactModalProps) => {
  const { t } = useTranslation();

  const [search, setSearch] = useState("");
  const [selectedContactId, setSelectedContactId] = useState<string | null>(
    null
  );

  const { contacts, isFetching } = useMarketingContacts(
    { page: 1, take: 20, search: search || undefined },
    { enabled: isOpen }
  );

  const { mutate: enrollContact, isLoading } = useEnrollMarketingFlowContact({
    onSuccess: () => {
      toast.success(t("Contact enrolled"));
      setSelectedContactId(null);
      onClose();
    },
    onError: toastServerError
  });

  const handleOk = () => {
    if (!selectedContactId) {
      toast.error(t("Pick a contact first"));
      return;
    }

    enrollContact({ flowId, contactId: selectedContactId });
  };

  return (
    <Modal
      isOpen={isOpen}
      title={t("Enroll contact")}
      contentClassName={styles.modalContent}
      isLoading={isLoading}
      okText={t("Enroll")}
      onClose={onClose}
      onOk={handleOk}
    >
      <div className={styles.form}>
        <Select
          label={t("Contact")}
          placeholder={t("Search by phone or name...")}
          options={contacts.map((contact) => ({
            label: contact.name
              ? `${contact.name} (+${contact.phoneNumber})`
              : `+${contact.phoneNumber}`,
            value: contact.id
          }))}
          value={selectedContactId}
          isOptionsLoading={isFetching}
          withFilterInput
          onFilterInputChange={setSearch}
          onChange={(selected) => setSelectedContactId(selected as string)}
        />

        <span className={styles.form__hint}>
          {t(
            "Enrollment is idempotent: a contact already in this flow will not be enrolled twice."
          )}
        </span>
      </div>
    </Modal>
  );
};

export default EnrollContactModal;
